import React, { useState, useRef, useEffect } from 'react';
import { inp, sectionDivider, sectionHeading, NoneCheckbox } from './shared';
import { searchPharmaciesByName } from '../../services/npiService';

const MPF_URL = 'https://mpf.translatinginsurance.com';

function PharmacySearch({ zip, state, onAdd }) {
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const loadedRef = useRef(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    loadedRef.current = false;
    setCatalog([]);
  }, [zip, state]);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function loadCatalog() {
    if (loadedRef.current || (!zip && !state)) return;
    const params = new URLSearchParams();
    if (zip)   params.set('zip_code', zip);
    if (state) params.set('state', state);
    params.set('radius', '40');
    setLoading(true);
    fetch(`${MPF_URL}/pharmacies/search?${params}`)
      .then(r => r.json())
      .then(data => { setCatalog(Array.isArray(data) ? data : []); loadedRef.current = true; })
      .catch(() => { loadedRef.current = true; })
      .finally(() => setLoading(false));
  }

  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);

    clearTimeout(searchTimerRef.current);
    const q = val.trim();
    if (q.length >= 2) {
      setSearching(true);
      searchTimerRef.current = setTimeout(async () => {
        try {
          const results = await searchPharmaciesByName(q, zip, state);
          setSearchResults(results);
        } catch {
          setSearchResults([]);
        }
        setSearching(false);
      }, 350);
    } else {
      setSearchResults([]);
      setSearching(false);
    }
  }

  function select(pharm) {
    onAdd({ Pharmacy_Name: pharm.name, Pharmacy_NPI: pharm.npi || null, Pharmacy_Address: pharm.address || '', Pharmacy_City: pharm.city || '', Pharmacy_State: pharm.state || '' });
    setQuery('');
    setSearchResults([]);
    setOpen(false);
  }

  function addManual() {
    const name = query.trim();
    if (!name) return;
    onAdd({ Pharmacy_Name: name, Pharmacy_NPI: null, Pharmacy_Address: '', Pharmacy_City: '', Pharmacy_State: '' });
    setQuery('');
    setSearchResults([]);
    setOpen(false);
  }

  const q = query.trim();
  const isLiveSearch = q.length >= 2;
  const isLoading = loading || (isLiveSearch && searching);
  const visible = isLiveSearch ? searchResults : catalog.slice(0, 30);
  const showCustom = isLiveSearch && visible.length === 0 && !isLoading;

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: 10 }}>
      <input
        style={{ ...inp, background: '#fff' }}
        value={query}
        placeholder={loading ? 'Loading nearby pharmacies…' : 'Search pharmacies by name…'}
        autoComplete="off"
        onChange={handleQueryChange}
        onFocus={() => { loadCatalog(); setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && showCustom) addManual(); }}
      />
      {open && (isLoading || visible.length > 0 || showCustom) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, zIndex: 200, maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2 }}>
          {isLoading && <div style={{ padding: '10px 14px', fontSize: 13, color: '#7a99b8' }}>{isLiveSearch ? 'Searching…' : 'Loading nearby pharmacies…'}</div>}
          {!isLoading && visible.map(pharm => (
            <div
              key={pharm.npi}
              onMouseDown={() => select(pharm)}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f4f8fc' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1a3364' }}>{pharm.name}</div>
              <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 1 }}>{[pharm.address, pharm.city, pharm.state, pharm.zip].filter(Boolean).join(', ')}</div>
            </div>
          ))}
          {showCustom && (
            <div onMouseDown={addManual} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#7a99b8', fontStyle: 'italic' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              Add "{query.trim()}" manually
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Pharmacy({ data, onChange }) {
  return (
    <div>
      <div style={sectionDivider}>
        <div style={sectionHeading}>Current Pharmacy</div>
        <p style={{ fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 14 }}>
          Search pharmacies near your ZIP. Click to add one or more.
        </p>
        <PharmacySearch
          zip={data.zip}
          state={data.state}
          onAdd={pharm => onChange('pharmacies', [...(data.pharmacies || []), pharm])}
        />
        {(data.pharmacies || []).filter(p => p && (p.name || p)).map((p, i) => {
          const name    = typeof p === 'object' ? (p.Pharmacy_Name || p.name || '') : p;
          const address = typeof p === 'object' ? [p.Pharmacy_Address || p.address, p.Pharmacy_City || p.city, p.Pharmacy_State || p.state].filter(Boolean).join(', ') : '';
          const npi     = typeof p === 'object' ? (p.Pharmacy_NPI || p.npi || null) : null;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>{name}</div>
                {address && <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 2 }}>{address}</div>}
                {npi && <div style={{ fontSize: 11, color: '#9ab0c8', marginTop: 1 }}>NPI: {npi}</div>}
              </div>
              <button type="button" onClick={() => onChange('pharmacies', (data.pharmacies || []).filter((_, idx) => idx !== i))} style={{ padding: '5px 10px', border: 'none', background: '#f5e8e8', borderRadius: 6, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 14, flexShrink: 0, marginLeft: 12 }}>
                ×
              </button>
            </div>
          );
        })}
        {(data.pharmacies || []).filter(p => p && (p.name || p)).length === 0 && (
          <NoneCheckbox
            checked={data.noPharmacies}
            onChange={v => onChange('noPharmacies', v)}
            label="I don't use a pharmacy"
          />
        )}
      </div>
    </div>
  );
}
